/**
 * GatewayFailoverCard (OCP-949) - "Provider failover": per gateway model group
 * the preferred and the active order, the state (preferred / failed over /
 * held) and since when, the last order change, the group alert, what the rules
 * would do now and what holds it back, plus the history of swaps, restores,
 * promotes, holds and preferred-order edits.
 *
 * OCP drives the `order` of every group from the per-deployment probe: an
 * unhealthy primary (failing for the fail streak, or slow for
 * ai.gateway_failover_slow_runs runs) is replaced by the best healthy sibling;
 * the preferred order comes back once its primary has been healthy for
 * ai.gateway_failover_recovery_minutes. Hold pins the active order (until it
 * is released, or until a time given with it, at most 7 days ahead), Promote makes
 * one deployment the primary now, and the switch turns the automatic failover
 * on or off - all three need a reason and are audited (the same REST routes the
 * MCP tools ocp_gateway_failover_hold / ocp_gateway_failover_promote /
 * ocp_gateway_failover_enabled use).
 */
import { useCallback, useEffect, useState } from 'react';

import { api, ApiError } from '../lib/api';
import { fmtDateTime } from '../lib/format-time';

type GroupState = 'preferred' | 'failed_over' | 'held';

interface OrderEntry { model_id: string; provider: string | null; order: number | null }

interface Member {
  model_id: string;
  provider: string | null;
  order: number | null;
  preferred_order: number | null;
  active: boolean;
  status: string | null;
  latency_ms: number | null;
  good: boolean;
  healthy_candidate: boolean;
  healthy_candidate_reason?: string;
  unhealthy: boolean;
  fail_streak: number;
  error_streak: number;
  recent_ok: number;
  recent_probes: number;
}

interface Group {
  model_name: string;
  kind: string;
  capability: string;
  failover_capable: boolean;
  not_capable_reason: string | null;
  state: GroupState;
  since: string | null;
  hold: { at: string; by: string | null; reason: string | null; source: string | null; until?: string | null } | null;
  preferred_order: OrderEntry[];
  active_order: OrderEntry[];
  active_model_id: string | null;
  preferred_model_id: string | null;
  last_swap: { at: string; kind: string | null; source: string | null; by: string | null; reason: string | null } | null;
  alert: { alerting: boolean; since: string | null; last_alerted_at: string | null; last_reason: string | null };
  /** OCP-1028: every deployment unhealthy but answering; the critical alert falls due at `push_due_at` (null once pushed). */
  slow_episode?: { since: string; minutes: number; limit_minutes: number; push_due_at: string | null; pushed: boolean } | null;
  write_alert?: { alerting: boolean; target: Record<string, number> | null };
  blocked?: { by: string; since: string | null; runs: number; announced: boolean } | null;
  pending: { action: 'swap' | 'restore' | 'repair'; candidate_model_id: string; blocked_by: string | null; ready_at: string | null } | null;
  members: Member[];
}

interface HistoryEvent {
  id: string;
  at: string;
  model_name: string;
  kind: string;
  source: string;
  actor: string;
  to_model_id: string | null;
  verified: boolean | null;
  error: string | null;
  reason: string;
}

interface FailoverStatus {
  enabled: boolean;
  settings: { fail_streak: number; slow_runs: number; cooldown_minutes: number; recovery_minutes: number; alert_cooldown_minutes: number; min_samples?: number; slow_alert_minutes?: number; slow_answer_max_ms?: number } | null;
  groups: Group[];
  history: HistoryEvent[];
}

const STATE_LABEL: Record<GroupState, string> = { preferred: 'Preferred', failed_over: 'Failed over', held: 'Held' };

const KIND_LABEL: Record<string, string> = {
  auto_swap: 'Automatic swap',
  auto_restore: 'Automatic restore',
  auto_repair: 'Automatic repair',
  manual_promote: 'Promote',
  hold: 'Hold',
  release: 'Release',
  preferred_order: 'Preferred order edited',
  failover_on: 'Automatic failover switched on',
  failover_off: 'Automatic failover switched off',
  slow_episode: 'Slow episode ended',
};

/** Hold at most this far ahead (the api refuses more). */
const MAX_HOLD_DAYS = 7;

/** A datetime-local value of the browser's local time, `ms` from now. */
function localInputValue(ms: number): string {
  const d = new Date(Date.now() + ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const BLOCKER_LABEL: Record<string, string> = {
  hold: 'held',
  disabled: 'automatic failover is off',
  cooldown: 'cooldown',
  gateway_fault: 'gateway-level fault',
};

function stateClasses(state: GroupState): string {
  if (state === 'preferred') return 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 ring-green-200 dark:ring-green-800';
  if (state === 'held') return 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 ring-gray-300 dark:ring-gray-700';
  return 'bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 ring-amber-200 dark:ring-amber-800';
}

function orderList(entries: OrderEntry[]): string {
  return [...entries]
    .sort((a, b) => (a.order ?? 999) - (b.order ?? 999))
    .map((e) => `${e.order ?? '-'} ${e.provider ?? e.model_id}`)
    .join(', ');
}

function providerOf(g: Group, id: string | null): string {
  if (!id) return '-';
  return g.members.find((m) => m.model_id === id)?.provider ?? id;
}

type Action = { kind: 'hold' | 'release' | 'promote'; modelName: string };

export function GatewayFailoverCard() {
  const [data, setData] = useState<FailoverStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [action, setAction] = useState<Action | null>(null);
  const [reason, setReason] = useState('');
  const [target, setTarget] = useState('');
  const [until, setUntil] = useState('');
  const [busy, setBusy] = useState(false);
  // The on / off switch: null = no dialog, otherwise the value it would set.
  const [switchTo, setSwitchTo] = useState<boolean | null>(null);
  const [switchReason, setSwitchReason] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api<FailoverStatus>('/v1/admin/gateway/failover?history=20');
      setData({ ...res, groups: Array.isArray(res?.groups) ? res.groups : [], history: Array.isArray(res?.history) ? res.history : [] });
    } catch (err) {
      setError(err instanceof ApiError ? `Could not load provider failover (HTTP ${err.status}).` : 'Could not load provider failover.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const open = (a: Action, g: Group) => {
    setAction(a);
    setReason('');
    setUntil('');
    setNotice(null);
    setTarget(g.members.find((m) => !m.active)?.model_id ?? '');
  };

  const submit = async () => {
    if (!action) return;
    setBusy(true);
    setError(null);
    try {
      const body: Record<string, string> = { model_name: action.modelName, reason: reason.trim() };
      if (action.kind === 'promote') body.model_id = target;
      if (action.kind === 'hold' && until) body.until = new Date(until).toISOString();
      const res = await api<{ changed: boolean; warning?: string | null }>(`/v1/admin/gateway/failover/${action.kind}`, { method: 'POST', body });
      setNotice(
        res.changed
          ? `${action.kind === 'hold' ? 'Held' : action.kind === 'release' ? 'Released' : 'Promoted'} ${action.modelName}.${res.warning ? ` ${res.warning}` : ''}`
          : `Nothing to change for ${action.modelName}.`,
      );
      setAction(null);
      await load();
    } catch (err) {
      const reasonText = err instanceof ApiError && err.body && typeof err.body === 'object' && 'reason' in err.body ? ` ${(err.body as { reason?: string }).reason}` : '';
      setError(err instanceof ApiError ? `${action.kind} failed (HTTP ${err.status}).${reasonText}` : `${action.kind} failed.`);
    } finally {
      setBusy(false);
    }
  };

  const submitSwitch = async () => {
    if (switchTo === null) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api<{ changed: boolean; enabled: boolean }>('/v1/admin/gateway/failover/enabled', { method: 'POST', body: { enabled: switchTo, reason: switchReason.trim() } });
      setNotice(res.changed ? `Automatic failover switched ${res.enabled ? 'on' : 'off'}.` : `Automatic failover was already ${res.enabled ? 'on' : 'off'}.`);
      setSwitchTo(null);
      setSwitchReason('');
      await load();
    } catch (err) {
      const reasonText = err instanceof ApiError && err.body && typeof err.body === 'object' && 'reason' in err.body ? ` ${(err.body as { reason?: string }).reason}` : '';
      setError(err instanceof ApiError ? `Switching the automatic failover failed (HTTP ${err.status}).${reasonText}` : 'Switching the automatic failover failed.');
    } finally {
      setBusy(false);
    }
  };

  const s = data?.settings ?? null;

  return (
    <section data-testid="gateway-failover-card" className="mt-6 rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Provider failover</h2>
          <p className="mt-1 max-w-2xl text-xs text-gray-500 dark:text-gray-400">
            orboto Control sets the order of every model group from the deployment probes. LiteLLM only moves on after an error or a timeout, so a slow primary would keep every call.
            {s
              ? ` A primary that fails for ${s.fail_streak} runs or is slow for ${s.slow_runs} runs is replaced by the best healthy sibling; the preferred order returns once its primary has been healthy for ${s.recovery_minutes} min. At most one change per group every ${s.cooldown_minutes} min. A group alerts when no deployment is healthy, or when a wanted swap is held back.${s.slow_alert_minutes !== undefined ? ` If every deployment still answers, only slowly, the alert waits ${s.slow_alert_minutes} min; a deployment that ${s.slow_answer_max_ms !== undefined ? `errors, times out or takes ${s.slow_answer_max_ms / 1000} s or longer to answer` : 'errors or times out'} alerts at once.` : ''}`
              : ''}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {data && (
            <button
              type="button"
              data-testid="failover-enabled-toggle"
              onClick={() => {
                setSwitchTo(!data.enabled);
                setSwitchReason('');
                setNotice(null);
              }}
              disabled={busy}
              className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-xs font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
            >
              {data.enabled ? 'Switch automatic failover off' : 'Switch automatic failover on'}
            </button>
          )}
          <button
            type="button"
            onClick={() => void load()}
            disabled={loading || busy}
            className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-xs font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
          >
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      {switchTo !== null && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={switchTo ? 'Switch automatic failover on' : 'Switch automatic failover off'}
          data-testid="failover-switch-dialog"
          className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 px-4"
          onClick={() => setSwitchTo(null)}
        >
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6 shadow-xl">
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
              {switchTo ? 'Switch automatic failover on' : 'Switch automatic failover off'}
            </h3>
            <p className="mt-2 text-xs text-gray-600 dark:text-gray-400">
              {switchTo
                ? 'OCP swaps, restores and repairs the order of every model group again from the next probe run.'
                : 'OCP makes no automatic order change in any model group. A group with no healthy deployment still alerts, and a wanted swap held back by the switch is warned about. Hold and Promote keep working.'}
            </p>
            <label className="mt-3 block text-sm">
              <span className="text-xs font-medium uppercase tracking-wide text-gray-500">Reason (audited, 10 to 500 characters)</span>
              <textarea
                aria-label="Switch reason"
                value={switchReason}
                onChange={(e) => setSwitchReason(e.target.value)}
                rows={3}
                maxLength={500}
                className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-2 py-1 text-sm text-gray-900 dark:text-gray-100"
              />
            </label>
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" onClick={() => setSwitchTo(null)} className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-xs text-gray-700 dark:text-gray-300">
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void submitSwitch()}
                disabled={busy || switchReason.trim().length < 10}
                className="rounded bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {busy ? 'Saving…' : switchTo ? 'Switch on' : 'Switch off'}
              </button>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div role="alert" className="mt-4 rounded border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}
      {notice && (
        <div data-testid="failover-notice" className="mt-4 rounded border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/30 px-3 py-2 text-sm text-green-800 dark:text-green-300">
          {notice}
        </div>
      )}
      {data && !data.enabled && (
        <div data-testid="failover-disabled" className="mt-4 rounded border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 px-3 py-2 text-sm text-amber-800 dark:text-amber-300">
          Automatic failover is switched off (<span className="font-mono">ai.gateway_failover_enabled</span>). Groups keep their order; a group with no healthy deployment still alerts, and a wanted swap held back by the switch is warned about.
        </div>
      )}
      {data && data.groups.length === 0 && <p className="mt-4 text-sm text-gray-500">No probed model group yet.</p>}

      {data && data.groups.length > 0 && (
        <div className="mt-4 overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-800 text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800/60 text-left text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-3 py-2">Model group</th>
                <th className="px-3 py-2">State</th>
                <th className="px-3 py-2">Active order</th>
                <th className="px-3 py-2">Preferred order</th>
                <th className="px-3 py-2">Last change</th>
                <th className="px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {data.groups.map((g) => (
                <tr key={g.model_name} data-testid="failover-group-row" className="align-top">
                  <td className="px-3 py-2">
                    <div className="font-mono text-gray-900 dark:text-gray-100">{g.model_name}</div>
                    <div className="text-xs text-gray-500">{g.capability}</div>
                    {g.alert.alerting && (
                      <div data-testid={`failover-alert-${g.model_name}`} className="mt-1 text-xs font-semibold text-red-700 dark:text-red-300">
                        No healthy deployment{g.alert.since ? ` since ${fmtDateTime(g.alert.since)}` : ''}
                      </div>
                    )}
                    {g.slow_episode && !g.slow_episode.pushed && (
                      <div data-testid={`failover-slow-${g.model_name}`} className="mt-1 max-w-xs text-xs text-amber-800 dark:text-amber-300">
                        Every deployment answers, only slowly, since {fmtDateTime(g.slow_episode.since)} ({g.slow_episode.minutes} of {g.slow_episode.limit_minutes} min).
                        {g.slow_episode.push_due_at ? ` The critical alert falls due ${fmtDateTime(g.slow_episode.push_due_at)} unless a deployment is good again.` : ''}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <span data-testid={`failover-state-${g.model_name}`} className={`inline-flex items-center rounded px-1.5 py-0.5 text-xs font-semibold uppercase tracking-wide ring-1 ring-inset ${stateClasses(g.state)}`}>
                      {STATE_LABEL[g.state]}
                    </span>
                    {g.since && <div className="mt-0.5 text-xs text-gray-500">since {fmtDateTime(g.since)}</div>}
                    {g.hold && (
                      <div className="mt-0.5 max-w-xs text-xs text-gray-600 dark:text-gray-400">
                        by {g.hold.by ?? '-'}: {g.hold.reason}
                        <div data-testid={`failover-hold-until-${g.model_name}`}>{g.hold.until ? `lapses ${fmtDateTime(g.hold.until)}` : 'until released'}</div>
                      </div>
                    )}
                    {g.write_alert?.alerting && (
                      <div data-testid={`failover-write-alert-${g.model_name}`} className="mt-0.5 max-w-xs text-xs font-semibold text-red-700 dark:text-red-300">
                        Last order write failed; the group is written again after the cooldown (target chosen then) until a write reads back
                      </div>
                    )}
                    {g.blocked?.announced && (
                      <div data-testid={`failover-blocked-${g.model_name}`} className="mt-0.5 max-w-xs text-xs text-amber-800 dark:text-amber-300">
                        Unhealthy primary stays: the swap is held back ({BLOCKER_LABEL[g.blocked.by] ?? g.blocked.by})
                      </div>
                    )}
                    {!g.failover_capable && (
                      <div className="mt-0.5 text-xs text-gray-500">
                        {g.not_capable_reason === 'single_deployment' ? 'one deployment, nothing to fail over to' : g.not_capable_reason === 'unordered' ? 'a deployment has no order' : 'orders are not distinct'}
                      </div>
                    )}
                    {g.pending && (
                      <div data-testid={`failover-pending-${g.model_name}`} className="mt-0.5 max-w-xs text-xs text-amber-800 dark:text-amber-300">
                        {g.pending.action === 'swap' ? `Swap to ${providerOf(g, g.pending.candidate_model_id)}` : g.pending.action === 'repair' ? 'Repair the live orders' : 'Restore the preferred order'}
                        {g.pending.blocked_by
                          ? ` held back: ${BLOCKER_LABEL[g.pending.blocked_by] ?? g.pending.blocked_by}${g.pending.ready_at ? ` until ${fmtDateTime(g.pending.ready_at)}` : ''}`
                          : ' on the next probe run'}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-700 dark:text-gray-300">
                    {orderList(g.active_order)}
                    <div className="mt-0.5 text-gray-500">
                      {g.members.map((m) => (
                        <div key={m.model_id}>
                          {m.provider ?? m.model_id}: {m.status ?? 'not probed'}{m.latency_ms !== null ? ` ${m.latency_ms} ms` : ''}, {m.recent_ok}/{m.recent_probes} ok
                          {m.unhealthy ? ' - unhealthy' : m.healthy_candidate ? '' : ` - not a candidate${m.healthy_candidate_reason ? `: ${m.healthy_candidate_reason}` : ''}`}
                        </div>
                      ))}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-700 dark:text-gray-300">{orderList(g.preferred_order)}</td>
                  <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400">
                    {g.last_swap ? (
                      <>
                        <div>{KIND_LABEL[g.last_swap.kind ?? ''] ?? g.last_swap.kind} ({g.last_swap.source})</div>
                        <div>{fmtDateTime(g.last_swap.at)}</div>
                        {g.last_swap.reason && <div className="max-w-xs break-words">{g.last_swap.reason}</div>}
                      </>
                    ) : '-'}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      {g.hold ? (
                        <button type="button" data-testid={`failover-release-${g.model_name}`} onClick={() => open({ kind: 'release', modelName: g.model_name }, g)} className="rounded border border-gray-300 dark:border-gray-700 px-2 py-1 text-xs font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800">
                          Release
                        </button>
                      ) : (
                        <button type="button" data-testid={`failover-hold-${g.model_name}`} onClick={() => open({ kind: 'hold', modelName: g.model_name }, g)} className="rounded border border-gray-300 dark:border-gray-700 px-2 py-1 text-xs font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800">
                          Hold
                        </button>
                      )}
                      {g.failover_capable && (
                        <button type="button" data-testid={`failover-promote-${g.model_name}`} onClick={() => open({ kind: 'promote', modelName: g.model_name }, g)} className="rounded border border-gray-300 dark:border-gray-700 px-2 py-1 text-xs font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800">
                          Promote
                        </button>
                      )}
                    </div>
                    {action && action.modelName === g.model_name && (
                      <div data-testid="failover-action-form" className="mt-2 w-64 space-y-2">
                        {action.kind === 'promote' && (
                          <label className="block">
                            <span className="text-xs font-medium uppercase tracking-wide text-gray-500">Deployment</span>
                            <select
                              aria-label="Deployment to promote"
                              value={target}
                              onChange={(e) => setTarget(e.target.value)}
                              className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-2 py-1 text-xs text-gray-900 dark:text-gray-100"
                            >
                              {g.members.filter((m) => !m.active).map((m) => (
                                <option key={m.model_id} value={m.model_id}>{`${m.provider ?? m.model_id} (order ${m.order ?? '-'})`}</option>
                              ))}
                            </select>
                          </label>
                        )}
                        {action.kind === 'hold' && (
                          <label className="block">
                            <span className="text-xs font-medium uppercase tracking-wide text-gray-500">Hold until (empty = until released, max {MAX_HOLD_DAYS} days)</span>
                            <input
                              type="datetime-local"
                              aria-label="Hold until"
                              value={until}
                              min={localInputValue(60_000)}
                              max={localInputValue(MAX_HOLD_DAYS * 86_400_000)}
                              onChange={(e) => setUntil(e.target.value)}
                              className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-2 py-1 text-xs text-gray-900 dark:text-gray-100"
                            />
                            <span className="mt-0.5 block text-xs text-gray-500">Empty = until released. The next probe run after it releases the hold.</span>
                          </label>
                        )}
                        <label className="block">
                          <span className="text-xs font-medium uppercase tracking-wide text-gray-500">Reason (audited)</span>
                          <input
                            aria-label="Reason"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-2 py-1 text-xs text-gray-900 dark:text-gray-100"
                          />
                        </label>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => void submit()}
                            disabled={busy || reason.trim().length < 3 || (action.kind === 'promote' && !target)}
                            className="rounded bg-brand-500 px-2 py-1 text-xs font-semibold text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {busy ? 'Saving…' : action.kind === 'hold' ? 'Hold order' : action.kind === 'release' ? 'Release hold' : 'Promote now'}
                          </button>
                          <button type="button" onClick={() => setAction(null)} className="rounded border border-gray-300 dark:border-gray-700 px-2 py-1 text-xs text-gray-700 dark:text-gray-300">
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data && data.history.length > 0 && (
        <div className="mt-4">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">History</h3>
          <ul data-testid="failover-history" className="mt-2 space-y-1 text-xs text-gray-700 dark:text-gray-300">
            {data.history.map((e) => (
              <li key={e.id}>
                <span className="text-gray-500">{fmtDateTime(e.at)}</span>{' '}
                <span className="font-mono">{e.model_name === '*' ? 'all groups' : e.model_name}</span>{' '}
                <span className="font-semibold">{KIND_LABEL[e.kind] ?? e.kind}</span> ({e.source}, {e.actor})
                {e.verified === false ? <span className="ml-1 font-semibold text-red-700 dark:text-red-300">write failed{e.error ? `: ${e.error}` : ''}</span> : null}
                <span className="text-gray-500"> - {e.reason}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
